import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { graphql } from '@/gql';
import type { CustomerIoTemplateKind } from '@/gql/graphql';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { FormField, FormLayout, FormSection } from '@/components/ui/FormLayout';
import { Select } from '@/components/ui/Select';
import { QueryErrorNotice } from '@/components/ui/QueryErrorNotice';
import { graphqlErrorMessage } from '@/lib/graphqlError';
import { CUSTOMER_IO_KIND_LABELS } from '@/lib/labels';

const KINDS: CustomerIoTemplateKind[] = [
  'MAGIC_LINK',
  'RECEIPT',
  'TICKET_MANAGE_CLAIM',
  'TICKET_CLAIM_REMINDER_HOLDER',
  'TICKET_QR',
  'COMPANION_TICKETS',
  'MEMBER_SALE_INVITE',
  'GUEST_LIST_INVITE',
  'VOUCHER',
  'CONTACT_GUEST',
  'CONTACT_STAFF',
  'PAYMENT_FAILED',
  'GOLDEN_TICKET_ASSIGNED',
  'CLAIM_LINK_REVOKED',
  'TICKET_HANDED_OVER',
  'VOUCHER_EXPIRY_REMINDER',
];

/**
 * What Ferry puts in `message_data` per template kind, mirrored from
 * `Go/internal/customerio/service/outbox.go` (`ProcessEmail`) and the
 * `emailReceipt` path in `service.go`. Customer.io exposes `message_data` to
 * Liquid as `{{trigger.<key>}}`, so these are the names a marketer types into
 * the template. Keep this in step with the Go payloads.
 */
const TEMPLATE_VARIABLES: Record<
  CustomerIoTemplateKind,
  { name: string; description: string }[]
> = {
  MAGIC_LINK: [
    {
      name: 'url',
      description: 'The sign-in link. This is the one the mail needs.',
    },
    {
      name: 'token',
      description: 'Raw token, only if you build the link yourself.',
    },
    {
      name: 'first_name',
      description: 'First name from the user row, when it is set.',
    },
  ],
  RECEIPT: [
    { name: 'receipt_url', description: 'Public receipt page.' },
    { name: 'venue', description: 'Venue public name.' },
    { name: 'total_cents', description: 'Order total in integer cents.' },
    { name: 'total_label', description: 'Order total in euros, same formatting as the receipt PDF.' },
  ],
  TICKET_MANAGE_CLAIM: [
    {
      name: 'url',
      description:
        "The recipient's manage page, /tickets/manage/m.… (one per address, whatever the number of tickets). This is the one the mail needs.",
    },
    {
      name: 'claim_token',
      description: 'Raw claim token, only if you build the link yourself.',
    },
    {
      name: 'ticket_id',
      description:
        'Ticket UUID. Absent on a scheduled reminder when only tickets already sent to friends remain.',
    },
    { name: 'night_id', description: 'Night UUID.' },
    { name: 'production_id', description: 'Old name of night_id, sent until the templates are republished.' },
    { name: 'night_name', description: 'Night name.' },
    { name: 'production_name', description: 'Old name of night_name, sent until the templates are republished.' },
    { name: 'scope_name', description: 'Venue public name.' },
    {
      name: 'starts_at',
      description: 'Start time in Europe/Amsterdam, like Sat 3 Oct · 23:00.',
    },
    { name: 'date_label', description: 'Date, like Thursday 27 August. Omitted when unknown.' },
    { name: 'start_time', description: 'Start time, like 22:30.' },
    { name: 'doors_open', description: 'Doors open, like 21:30. Omitted when the night has no doors.' },
    { name: 'doors_close', description: 'Doors close, like 04:00. Omitted when unset.' },
    { name: 'venue_name', description: 'Venue public name. Same string as scope_name.' },
    { name: 'venue_address', description: 'Venue address, like Westblaak 127, 3012 KJ Rotterdam.' },
    { name: 'minimum_age', description: 'Minimum age, like 18. Omitted when unset.' },
    { name: 'stage_name', description: 'Ticket type, like Regular. Omitted when there is no stage.' },
    { name: 'event_url', description: 'Public event page on the website.' },
    {
      name: 'ticket_count',
      description:
        'How many tickets this order holds for the night the mail is about. An order with several nights sends one mail per night. On a scheduled reminder: the tickets this address paid for on the night.',
    },
    {
      name: 'reminder_stage',
      description:
        'Scheduled reminders only: week, two_days or one_hour before the night. Absent on the 10-minute nudge, so branch on {% if trigger.reminder_stage == "week" %}.',
    },
    {
      name: 'hours_to_start',
      description:
        'Scheduled reminders only: whole hours until the night starts, rounded.',
    },
    {
      name: 'unclaimed_count',
      description:
        'Scheduled reminders only: tickets this address paid for that are still purchased (not claimed).',
    },
    {
      name: 'unsent_count',
      description:
        'Scheduled reminders only: purchased tickets nobody has been sent yet.',
    },
    {
      name: 'sent_unclaimed_count',
      description:
        'Scheduled reminders only: tickets sent to a friend who has not claimed them.',
    },
    {
      name: 'own_unclaimed',
      description:
        "Scheduled reminders only: true while the buyer's own ticket is not claimed yet (a boolean).",
    },
  ],
  TICKET_CLAIM_REMINDER_HOLDER: [
    {
      name: 'url',
      description:
        "The friend's own claim link, /tickets/claim/{token}. This is the one the mail needs.",
    },
    {
      name: 'manage_url',
      description:
        "The friend's own manage page. Omitted when the signing key is missing.",
    },
    {
      name: 'claim_token',
      description: 'Raw claim token, only if you build the link yourself.',
    },
    { name: 'ticket_id', description: 'The ticket the claim link is for.' },
    { name: 'night_id', description: 'Night UUID.' },
    { name: 'production_id', description: 'Old name of night_id, sent until the templates are republished.' },
    { name: 'night_name', description: 'Night name.' },
    { name: 'production_name', description: 'Old name of night_name, sent until the templates are republished.' },
    { name: 'scope_name', description: 'Venue public name.' },
    {
      name: 'starts_at',
      description: 'Start time in Europe/Amsterdam, like Sat 3 Oct · 23:00.',
    },
    { name: 'date_label', description: 'Date, like Thursday 27 August. Omitted when unknown.' },
    { name: 'start_time', description: 'Start time, like 22:30.' },
    { name: 'doors_open', description: 'Doors open, like 21:30. Omitted when the night has no doors.' },
    { name: 'doors_close', description: 'Doors close, like 04:00. Omitted when unset.' },
    { name: 'venue_name', description: 'Venue public name. Same string as scope_name.' },
    { name: 'venue_address', description: 'Venue address, like Westblaak 127, 3012 KJ Rotterdam.' },
    { name: 'minimum_age', description: 'Minimum age, like 18. Omitted when unset.' },
    { name: 'stage_name', description: 'Ticket type, like Regular. Omitted when there is no stage.' },
    { name: 'event_url', description: 'Public event page on the website.' },
    {
      name: 'reminder_stage',
      description:
        'Always set: week, two_days or one_hour before the night. Branch on {% if trigger.reminder_stage == "week" %}.',
    },
    {
      name: 'hours_to_start',
      description: 'Whole hours until the night starts, rounded.',
    },
    {
      name: 'ticket_count',
      description: 'How many tickets this address holds for the night.',
    },
    {
      name: 'unclaimed_count',
      description:
        'How many of those tickets this address has not claimed yet.',
    },
  ],
  TICKET_QR: [
    { name: 'ticket_id', description: 'Ticket UUID.' },
    { name: 'night_id', description: 'Night UUID.' },
    { name: 'production_id', description: 'Old name of night_id, sent until the templates are republished.' },
    { name: 'night_name', description: 'Night name.' },
    { name: 'production_name', description: 'Old name of night_name, sent until the templates are republished.' },
    { name: 'scope_name', description: 'Venue public name.' },
    {
      name: 'starts_at',
      description: 'Start time in Europe/Amsterdam, like Sat 3 Oct · 23:00.',
    },
    {
      name: 'first_name',
      description: 'First name from the claim. The door QR is attached as ticket.pdf.',
    },
    { name: 'holder_name', description: 'First and last name from the claim.' },
    { name: 'stage_name', description: 'Ticket type, like Regular.' },
    { name: 'date_label', description: 'Date, like Thursday 27 August.' },
    { name: 'start_time', description: 'Start time, like 22:30.' },
    { name: 'doors_open', description: 'Doors open, like 21:30. Omitted when unset.' },
    { name: 'doors_close', description: 'Doors close, like 04:00. Omitted when unset.' },
    { name: 'venue_name', description: 'Venue public name.' },
    { name: 'venue_address', description: 'Venue address.' },
    { name: 'minimum_age', description: 'Minimum age. Omitted when unset.' },
    { name: 'event_url', description: 'Public event page on the website.' },
    { name: 'app_tickets_url', description: 'Tickets tab in the app.' },
    {
      name: 'qr_url',
      description:
        'PNG of the door QR (ferry:ticket:{uuid}). 404 unless the ticket is claimed. Put this in the template as an image. Also attached as ticket.pdf.',
    },
    {
      name: 'apple_wallet_url',
      description: 'Apple Wallet link, when Wallet is configured.',
    },
    {
      name: 'google_wallet_url',
      description: 'Google Wallet link, when Wallet is configured.',
    },
    {
      name: 'entry_window_label',
      description:
        'Entry window of the ticket, like Entry before 21:00, Entry after 23:00 or Entry 22:30–02:00. Only when the ticket\'s stage has one. Guard with {% if trigger.entry_window_label %}.',
    },
    {
      name: 'entry_starts_at',
      description:
        'Entry window start as HH:MM Europe/Amsterdam; the doors open time when the stage leaves it unset. Only when the stage has an entry window.',
    },
    {
      name: 'entry_ends_at',
      description:
        'Entry window end as HH:MM Europe/Amsterdam; the doors close time when the stage leaves it unset. Only when the stage has an entry window.',
    },
    {
      name: 'voucher_offer_venue',
      description:
        'Venue that honours the free-drink voucher, like Elio. Only when the ticket earned a voucher. Guard with {% if trigger.voucher_offer_venue %}.',
    },
    {
      name: 'voucher_offer_text',
      description:
        'What the voucher is worth, like €5 off. Only when the ticket earned a voucher.',
    },
    {
      name: 'voucher_offer_until',
      description:
        'Last trading day it can be used, like Sat 31 Oct 2026. Only when the ticket earned a voucher.',
    },
    {
      name: 'manage_url',
      description:
        "The holder's own manage page. Omitted when the signing key is missing.",
    },
  ],
  COMPANION_TICKETS: [
    {
      name: 'url',
      description:
        'Claim link for the companion. This is the one the mail needs.',
    },
    {
      name: 'claim_token',
      description: 'Raw claim token, only if you build the link yourself.',
    },
    { name: 'ticket_id', description: 'Ticket UUID.' },
    { name: 'night_id', description: 'Night UUID.' },
    { name: 'production_id', description: 'Old name of night_id, sent until the templates are republished.' },
    { name: 'night_name', description: 'Night name.' },
    { name: 'production_name', description: 'Old name of night_name, sent until the templates are republished.' },
    { name: 'scope_name', description: 'Venue public name.' },
    {
      name: 'starts_at',
      description: 'Start time in Europe/Amsterdam, like Sat 3 Oct · 23:00.',
    },
    { name: 'date_label', description: 'Date, like Thursday 27 August.' },
    { name: 'start_time', description: 'Start time, like 22:30.' },
    { name: 'doors_open', description: 'Doors open. Omitted when unset.' },
    { name: 'doors_close', description: 'Doors close. Omitted when unset.' },
    { name: 'venue_name', description: 'Venue public name.' },
    { name: 'venue_address', description: 'Venue address.' },
    { name: 'minimum_age', description: 'Minimum age. Omitted when unset.' },
    { name: 'stage_name', description: 'Ticket type. Omitted when there is no stage.' },
    { name: 'event_url', description: 'Public event page on the website.' },
    {
      name: 'ticket_count',
      description:
        'How many tickets this order holds for the night the mail is about. An order with several nights sends one mail per night.',
    },
    { name: 'app_tickets_url', description: 'Tickets tab in the app.' },
    {
      name: 'manage_url',
      description:
        "The friend's own manage page. Omitted when the signing key is missing.",
    },
  ],
  MEMBER_SALE_INVITE: [
    {
      name: 'url',
      description: 'Secret checkout link. This is the one the mail needs.',
    },
    { name: 'night_id', description: 'Night UUID.' },
    { name: 'production_id', description: 'Old name of night_id, sent until the templates are republished.' },
    {
      name: 'ticket_id',
      description: 'The night stage (phase) UUID, not a ticket.',
    },
    { name: 'night_name', description: 'Night name.' },
    { name: 'production_name', description: 'Old name of night_name, sent until the templates are republished.' },
    {
      name: 'starts_at',
      description: 'Start time in Europe/Amsterdam, like Sat 3 Oct · 23:00.',
    },
    { name: 'date_label', description: 'Date, like Thursday 27 August.' },
    { name: 'start_time', description: 'Start time, like 22:30.' },
    { name: 'doors_open', description: 'Doors open. Omitted when unset.' },
    { name: 'doors_close', description: 'Doors close. Omitted when unset.' },
    { name: 'venue_name', description: 'Venue public name.' },
    { name: 'venue_address', description: 'Venue address.' },
    { name: 'minimum_age', description: 'Minimum age. Omitted when unset.' },
    { name: 'event_url', description: 'Public event page on the website.' },
  ],
  GUEST_LIST_INVITE: [
    {
      name: 'url',
      description:
        'Invite link. This is the one the mail needs. Not marketing consent.',
    },
    { name: 'night_id', description: 'Night UUID.' },
    { name: 'production_id', description: 'Old name of night_id, sent until the templates are republished.' },
    {
      name: 'ticket_id',
      description: 'The guest-list entry id, not a ticket.',
    },
    { name: 'night_name', description: 'Night name.' },
    { name: 'production_name', description: 'Old name of night_name, sent until the templates are republished.' },
    {
      name: 'starts_at',
      description: 'Start time in Europe/Amsterdam, like Sat 3 Oct · 23:00.',
    },
    { name: 'date_label', description: 'Date, like Thursday 27 August.' },
    { name: 'start_time', description: 'Start time, like 22:30.' },
    { name: 'doors_open', description: 'Doors open. Omitted when unset.' },
    { name: 'doors_close', description: 'Doors close. Omitted when unset.' },
    { name: 'venue_name', description: 'Venue public name.' },
    { name: 'venue_address', description: 'Venue address.' },
    { name: 'minimum_age', description: 'Minimum age. Omitted when unset.' },
    { name: 'event_url', description: 'Public event page on the website.' },
    {
      name: 'manage_url',
      description:
        "The host's own manage page, so they can see and send their seats before claiming.",
    },
  ],
  VOUCHER: [
    {
      name: 'qr_url',
      description:
        'PNG of the voucher QR (same payload the bar scans). Put this in the template as an image: <img src="{{trigger.qr_url}}" width="240" height="240" alt="Show this QR at the bar">. Also attached as voucher-qr.png if the mail client blocks remote images.',
    },
    { name: 'voucher_id', description: 'Voucher UUID — this is also the QR payload.' },
    { name: 'description', description: 'Benefit copy snapshotted onto the voucher.' },
    { name: 'scope_name', description: 'Venue public name.' },
    { name: 'expires_at', description: 'Expiry instant (ISO text), when set.' },
    {
      name: 'expires_label',
      description: 'Expiry date in Europe/Amsterdam, like Sat 3 Oct 2026, when set.',
    },
    {
      name: 'apple_wallet_url',
      description: 'Apple Wallet link, when Wallet is configured.',
    },
    {
      name: 'google_wallet_url',
      description: 'Google Wallet link, when Wallet is configured.',
    },
  ],
  CONTACT_GUEST: [
    { name: 'name', description: 'Name the guest typed.' },
    { name: 'subject', description: 'Subject they picked.' },
    { name: 'scope_name', description: 'Venue public name.' },
  ],
  CONTACT_STAFF: [
    { name: 'name', description: 'Name the guest typed.' },
    { name: 'email', description: 'Address they typed. Also the reply-to.' },
    { name: 'phone', description: 'Phone, when they typed one.' },
    { name: 'subject', description: 'Subject they picked.' },
    { name: 'message', description: 'The remark.' },
    { name: 'scope_name', description: 'Venue public name.' },
  ],
  PAYMENT_FAILED: [
    { name: 'night_name', description: 'Night name.' },
    { name: 'production_name', description: 'Old name of night_name, sent until the templates are republished.' },
    {
      name: 'starts_at',
      description: 'Start time in Europe/Amsterdam, like Sat 3 Oct · 23:00.',
    },
    { name: 'date_label', description: 'Date, like Thursday 27 August.' },
    { name: 'start_time', description: 'Start time, like 22:30.' },
    { name: 'doors_open', description: 'Doors open. Omitted when unset.' },
    { name: 'doors_close', description: 'Doors close. Omitted when unset.' },
    { name: 'venue_name', description: 'Venue public name.' },
    { name: 'venue_address', description: 'Venue address.' },
    { name: 'minimum_age', description: 'Minimum age. Omitted when unset.' },
    { name: 'event_url', description: 'Public event page on the website.' },
    { name: 'url', description: 'Public ticket page for that night.' },
  ],
  GOLDEN_TICKET_ASSIGNED: [
    { name: 'first_name', description: 'First name on the user, when it is set.' },
    { name: 'app_url', description: 'Public app origin. No QR, no golden-ticket id, no Wallet link.' },
  ],
  CLAIM_LINK_REVOKED: [
    { name: 'night_name', description: 'Night name.' },
    { name: 'production_name', description: 'Old name of night_name, sent until the templates are republished.' },
    { name: 'scope_name', description: 'Venue public name.' },
    {
      name: 'starts_at',
      description: 'Start time in Europe/Amsterdam, like Sat 3 Oct · 23:00.',
    },
    { name: 'date_label', description: 'Date, like Thursday 27 August.' },
    { name: 'start_time', description: 'Start time, like 22:30.' },
    { name: 'doors_open', description: 'Doors open. Omitted when unset.' },
    { name: 'doors_close', description: 'Doors close. Omitted when unset.' },
    { name: 'venue_name', description: 'Venue public name.' },
    { name: 'venue_address', description: 'Venue address.' },
    { name: 'minimum_age', description: 'Minimum age. Omitted when unset.' },
    { name: 'event_url', description: 'Public event page on the website.' },
  ],
  // The previous holder (and the buyer, when different) is told the ticket changed hands.
  // Event keys only: no link, no ticket_id, no QR.
  TICKET_HANDED_OVER: [
    { name: 'night_name', description: 'Night name.' },
    { name: 'production_name', description: 'Old name of night_name, sent until the templates are republished.' },
    { name: 'scope_name', description: 'Venue public name.' },
    {
      name: 'starts_at',
      description: 'Start time in Europe/Amsterdam, like Sat 3 Oct · 23:00.',
    },
    { name: 'date_label', description: 'Date, like Thursday 27 August.' },
    { name: 'start_time', description: 'Start time, like 22:30.' },
    { name: 'doors_open', description: 'Doors open. Omitted when unset.' },
    { name: 'doors_close', description: 'Doors close. Omitted when unset.' },
    { name: 'venue_name', description: 'Venue public name.' },
    { name: 'venue_address', description: 'Venue address.' },
    { name: 'minimum_age', description: 'Minimum age. Omitted when unset.' },
    { name: 'stage_name', description: 'Ticket type. Omitted when there is no stage.' },
    { name: 'event_url', description: 'Public event page on the website.' },
  ],
  VOUCHER_EXPIRY_REMINDER: [
    {
      name: 'qr_url',
      description:
        'Same voucher QR PNG as the issue mail. Also attached as voucher-qr.png.',
    },
    { name: 'voucher_id', description: 'Voucher UUID — this is also the QR payload.' },
    { name: 'description', description: 'Benefit copy snapshotted onto the voucher.' },
    { name: 'scope_name', description: 'Venue public name.' },
    { name: 'expires_at', description: 'Expiry instant (ISO text), when set.' },
    {
      name: 'expires_label',
      description: 'Expiry date in Europe/Amsterdam, like Sat 3 Oct 2026, when set.',
    },
    {
      name: 'apple_wallet_url',
      description: 'Apple Wallet link, when Wallet is configured.',
    },
    {
      name: 'google_wallet_url',
      description: 'Google Wallet link, when Wallet is configured.',
    },
  ],
};

function TemplateVariables({ kind }: { kind: CustomerIoTemplateKind }) {
  const vars = TEMPLATE_VARIABLES[kind] ?? [];
  if (vars.length === 0) return null;
  return (
    <dl className="mt-2 space-y-1">
      {vars.map((v) => (
        <div key={v.name} className="flex flex-wrap items-baseline gap-x-2">
          <dt>
            <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-800 dark:bg-gray-800 dark:text-gray-200">
              {`{{trigger.${v.name}}}`}
            </code>
          </dt>
          <dd className="text-xs text-gray-500 dark:text-gray-400">
            {v.description}
          </dd>
        </div>
      ))}
    </dl>
  );
}

const CustomerIoTemplatesDoc = graphql(/* GraphQL */ `
  query CustomerIoTemplates {
    customerIoTemplates {
      kind
      transactionalMessageId
      templateName
      configured
      updatedAt
    }
  }
`);

const CustomerIoTransactionalTemplatesDoc = graphql(/* GraphQL */ `
  query CustomerIoTransactionalTemplates {
    customerIoTransactionalTemplates {
      id
      name
      state
      sendToUnsubscribed
    }
  }
`);

const SaveCustomerIoTemplatesDoc = graphql(/* GraphQL */ `
  mutation SaveCustomerIoTemplates($input: [CustomerIoTemplateInput!]!) {
    saveCustomerIoTemplates(input: $input) {
      kind
      transactionalMessageId
      templateName
      configured
    }
  }
`);

export function CustomerIoTab() {
  const mappingsQuery = useQuery(CustomerIoTemplatesDoc, {
    errorPolicy: 'all',
  });
  const listQuery = useQuery(CustomerIoTransactionalTemplatesDoc, {
    errorPolicy: 'all',
  });
  const mappings = mappingsQuery.data?.customerIoTemplates ?? [];
  const templates = useMemo(
    () => listQuery.data?.customerIoTransactionalTemplates ?? [],
    [listQuery.data?.customerIoTransactionalTemplates],
  );

  const [draft, setDraft] = useState<Record<string, string>>({});
  const [seeded, setSeeded] = useState(false);
  if (mappings.length > 0 && !seeded) {
    setSeeded(true);
    const next: Record<string, string> = {};
    for (const m of mappings) {
      next[m.kind] =
        m.transactionalMessageId != null
          ? String(m.transactionalMessageId)
          : '';
    }
    setDraft(next);
  }

  const options = useMemo(
    () => [
      { value: '', label: 'Not mapped (fail closed)' },
      ...templates.map((t) => ({
        value: String(t.id),
        label: `${t.name} (#${t.id})${t.state ? ` · ${t.state}` : ''}${
          t.sendToUnsubscribed === false
            ? ' · missing send-to-unsubscribed'
            : ''
        }`,
      })),
    ],
    [templates],
  );

  const [save, { loading: saving }] = useMutation(SaveCustomerIoTemplatesDoc);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaveError(null);
    setSaved(false);
    try {
      await save({
        variables: {
          input: KINDS.map((kind) => {
            const raw = draft[kind] ?? '';
            const id = raw ? Number.parseInt(raw, 10) : null;
            const tpl = templates.find((t) => t.id === id);
            return {
              kind,
              transactionalMessageId: id,
              templateName: tpl?.name ?? null,
            };
          }),
        },
      });
      setSaved(true);
      await mappingsQuery.refetch();
    } catch (err) {
      setSaveError(graphqlErrorMessage(err));
    }
  };

  const mappingFor = (kind: CustomerIoTemplateKind) =>
    mappings.find((m) => m.kind === kind);

  if (mappingsQuery.loading && !mappingsQuery.data && !mappingsQuery.error) {
    return (
      <p className="text-sm text-gray-500">Loading Customer.io templates…</p>
    );
  }

  return (
    <FormLayout
      title="Transactional templates"
      description={
        <>
          Global mapping to Customer.io transactional_message_id values. Sending
          a kind with no template fails closed — Ferry never posts an
          uncategorized body. API keys stay in Secret Manager, not here. The
          list API does not expose the template Subject, so a missing Subject
          only shows up at send time in Go logs — set Subject on each template
          in Customer.io.
          <br />
          <br />
          Ferry sends each mail&apos;s data as Customer.io{' '}
          <code className="font-mono text-xs">message_data</code>, which Liquid
          reads as{' '}
          <code className="font-mono text-xs">{'{{trigger.<key>}}'}</code> — not{' '}
          <code className="font-mono text-xs">{'{{message_data.<key>}}'}</code>.
          A template that omits the variable still sends, just without the link,
          so the variables each kind receives are listed below it. Profile
          attributes such as{' '}
          <code className="font-mono text-xs">{'{{customer.first_name}}'}</code>{' '}
          also work, because Ferry identifies the profile before sending.
        </>
      }
      onSubmit={handleSave}
      footer={
        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="submit"
            loading={saving}
            disabled={Boolean(listQuery.error)}
          >
            Save templates
          </Button>
          {saved ? (
            <span className="text-sm text-emerald-600">Saved</span>
          ) : null}
          {saveError ? (
            <span className="text-sm text-red-600">{saveError}</span>
          ) : null}
        </div>
      }
    >
      <FormSection title="Customer.io">
        {mappingsQuery.error ? (
          <QueryErrorNotice
            error={mappingsQuery.error}
            title="Could not load stored template mappings"
          />
        ) : null}
        {listQuery.error ? (
          <QueryErrorNotice
            error={listQuery.error}
            title="Could not list Customer.io transactional templates"
          />
        ) : null}
        {listQuery.error ? (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            The template picker needs a working App API key on the Go backend.
            Until that is configured, kinds cannot be mapped from this screen.
            Unmapped kinds stay fail-closed.
          </p>
        ) : null}
        {KINDS.map((kind) => {
          const mapping = mappingFor(kind);
          const selected = draft[kind] ?? '';
          const incomplete = !selected;
          const tpl = templates.find((t) => String(t.id) === selected);
          const state = tpl?.state?.trim() ?? '';
          const draftOrNotReady =
            Boolean(selected) &&
            Boolean(state) &&
            state.toLowerCase() !== 'active';
          const missingUnsub = tpl?.sendToUnsubscribed === false;
          return (
            <FormField
              key={kind}
              label={CUSTOMER_IO_KIND_LABELS[kind] ?? kind}
              description={
                <>
                  <span>
                    {mapping?.templateName
                      ? `Currently: ${mapping.templateName}`
                      : 'No template selected — this mail cannot send.'}
                  </span>
                  <TemplateVariables kind={kind} />
                </>
              }
            >
              <div className="flex items-center gap-3">
                <Select
                  value={selected}
                  onChange={(e) =>
                    setDraft((prev) => ({ ...prev, [kind]: e.target.value }))
                  }
                  options={
                    options.length > 1
                      ? options
                      : [
                          { value: '', label: 'Not mapped (fail closed)' },
                          ...(mapping?.transactionalMessageId != null
                            ? [
                                {
                                  value: String(mapping.transactionalMessageId),
                                  label: mapping.templateName
                                    ? `${mapping.templateName} (#${mapping.transactionalMessageId})`
                                    : `#${mapping.transactionalMessageId}`,
                                },
                              ]
                            : []),
                        ]
                  }
                  disabled={Boolean(listQuery.error) && templates.length === 0}
                />
                {incomplete ? (
                  <Badge tone="warning">Incomplete</Badge>
                ) : draftOrNotReady ? (
                  <Badge tone="warning">{state}</Badge>
                ) : missingUnsub ? (
                  <Badge tone="warning">Missing send-to-unsubscribed</Badge>
                ) : (
                  <Badge tone="success">Mapped</Badge>
                )}
              </div>
            </FormField>
          );
        })}
      </FormSection>
    </FormLayout>
  );
}
